import {
  Column,
  ColumnDef,
  ColumnPinningState,
  Row,
  RowSelectionState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import { Dispatch, ReactNode, SetStateAction, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";

import { Icon } from "~/assets/icons/Icon";
import { Pagination } from "~/components/ui/pagination/Pagination";
import { SearchBar } from "~/components/ui/search/SearchBar";
import { useStagedBoolean } from "~/hooks/useStagedBoolean";

import styles from "./DataTable.module.scss";

export { ActionColumn } from "./ActionColumn";

type ColumnAlign = "left" | "center" | "right";

export type DataTableColumnMeta = {
  align?: ColumnAlign;
  /** Skip the default column size and let the column auto-size to the
   * remaining space. TanStack Table merges a default `size: 150` into every
   * column; setting this flag tells the DataTable to ignore that and not
   * apply an inline width. */
  autoWidth?: boolean;
  /** Allow this cell's content to overflow visually on hover, the way
   * Google Sheets lets a long value spill over adjacent cells. The cell
   * gets `overflow: visible` and a bumped z-index so its content can
   * overlay subsequent cells. The cell content itself is responsible
   * for switching to `width: max-content` (and giving itself an opaque
   * background) on hover so the overlap looks like an expansion. */
  expandOnHover?: boolean;
  /** Custom skeleton renderer for this column. When omitted, the
   * loading state shows a single full-width <Skeleton />. Override
   * this when the real cell renders a multi-line layout (e.g. a
   * keyword + URL stack) so the skeleton row reserves the same height
   * and the content swap doesn't shift the layout. */
  skeleton?: () => ReactNode;
  cellClassName?: string;
};

type DataTableProps<T> = {
  columns: ColumnDef<T>[];
  data: T[];
  isLoading: boolean;
  skeletonRows?: number;
  /** Suppress the skeleton placeholder rows during `isLoading`. The
   * existing rows (if any) stay rendered, and the refetch / pagination
   * spinner is the only loading indicator. Useful for tables where
   * skeleton flashes during pagination feel more disruptive than a
   * brief content swap. */
  disableSkeleton?: boolean;
  emptyText?: string;
  pageSize?: number;
  initialSorting?: SortingState;
  tableLayout?: "auto" | "fixed";
  onRefetch?: () => void;
  isRefetching?: boolean;
  toolbarActions?: React.ReactNode;
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
  };
  externalSorting?: {
    state: SortingState;
    onChange: (state: SortingState) => void;
  };
  rowSelection?: {
    state: RowSelectionState;
    onChange: Dispatch<SetStateAction<RowSelectionState>>;
    enabled?: boolean | ((row: Row<T>) => boolean);
  };
  columnPinning?: ColumnPinningState;
};

function getPinningStyle<T>(column: Column<T>): React.CSSProperties {
  const isPinned = column.getIsPinned();
  if (!isPinned) return {};

  return {
    position: "sticky",
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    zIndex: 1,
    opacity: 0.95,
    borderRight:
      isPinned === "left" ? "1px solid var(--functional-grey3)" : undefined,
    borderLeft:
      isPinned === "right" ? "1px solid var(--functional-grey3)" : undefined,
  };
}

function DataTable<T>({
  columns,
  data,
  isLoading,
  skeletonRows = 8,
  disableSkeleton = false,
  emptyText = "No data available.",
  pageSize,
  initialSorting = [],
  tableLayout = "fixed",
  onRefetch,
  isRefetching,
  toolbarActions,
  search,
  externalSorting,
  rowSelection,
  columnPinning,
}: DataTableProps<T>) {
  const [internalSorting, setInternalSorting] =
    useState<SortingState>(initialSorting);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: pageSize ?? 10,
  });

  const currentSorting = externalSorting?.state ?? internalSorting;
  const showRefetchSpinner = useStagedBoolean(isRefetching ?? false, 0, 700);

  // Track the latest sorting value in a ref so rapid clicks within
  // the same React batch each see the result of the PREVIOUS click,
  // not the stale render-time value. Without this, the 3-state cycle
  // (asc → desc → remove) degrades to (asc ↔ desc) under rapid
  // clicking because every click resolves TanStack's updater against
  // the same stale `currentSorting`.
  const sortingRef = useRef(currentSorting);
  sortingRef.current = currentSorting;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualSorting: !!externalSorting,
    initialState: {
      ...(columnPinning && { columnPinning }),
    },
    state: {
      sorting: currentSorting,
      pagination:
        pageSize !== undefined
          ? pagination
          : { pageIndex: 0, pageSize: data.length },
      ...(rowSelection && { rowSelection: rowSelection.state }),
    },
    onSortingChange: externalSorting
      ? (updater) => {
          const prev = sortingRef.current;
          const next = typeof updater === "function" ? updater(prev) : updater;
          sortingRef.current = next;
          externalSorting.onChange(next);
        }
      : setInternalSorting,
    onPaginationChange: setPagination,
    manualPagination: false,
    isMultiSortEvent: () => true,
    ...(rowSelection && {
      enableRowSelection: rowSelection.enabled ?? true,
      onRowSelectionChange: rowSelection.onChange as Dispatch<
        SetStateAction<RowSelectionState>
      >,
    }),
  });

  const showPagination =
    pageSize !== undefined && !isLoading && data.length > 0;

  const getMeta = (columnDef: ColumnDef<T>): DataTableColumnMeta =>
    (columnDef.meta as DataTableColumnMeta | undefined) ?? {};

  const getAlign = (columnDef: ColumnDef<T>): ColumnAlign =>
    getMeta(columnDef).align ?? "left";

  return (
    <div>
      {(search !== undefined ||
        onRefetch !== undefined ||
        toolbarActions !== undefined) && (
        <div className={styles.toolbar}>
          {(search !== undefined || onRefetch !== undefined) && (
            <div className={styles.searchGroup}>
              {search !== undefined && (
                <SearchBar
                  value={search.value}
                  onChange={search.onChange}
                  placeholder={search.placeholder}
                />
              )}
              {onRefetch !== undefined && (
                <div className={styles.refetchButtonOuter}>
                  <button
                    className={styles.refetchButton}
                    onClick={onRefetch}
                    disabled={showRefetchSpinner}
                    title="Refresh"
                    type="button"
                  >
                    <span
                      className={clsx(styles.refetchIcon, {
                        [styles.spinning]: showRefetchSpinner,
                      })}
                    >
                      <Icon iconName="RefreshIcon" width="1rem" height="1rem" />
                    </span>
                  </button>
                </div>
              )}
            </div>
          )}
          {toolbarActions !== undefined && (
            <div className={styles.toolbarActions}>{toolbarActions}</div>
          )}
        </div>
      )}
      <div className={styles.wrapper}>
        <table
          className={styles.table}
          style={tableLayout === "fixed" ? { tableLayout } : undefined}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const align = getAlign(header.column.columnDef);
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const sortIndex = currentSorting.findIndex(
                    (s) => s.id === header.column.id,
                  );
                  const size = header.column.columnDef.size;
                  const autoWidth =
                    (
                      header.column.columnDef.meta as
                        | DataTableColumnMeta
                        | undefined
                    )?.autoWidth ?? false;

                  // For external sorting, bypass TanStack's built-in
                  // toggle and cycle through the 3-state sort directly
                  // against the ref-tracked latest state. For internal
                  // sorting, use TanStack's handler directly so it
                  // receives the real MouseEvent.
                  const handleExternalSortClick = canSort
                    ? () => {
                        const colId = header.column.id;
                        const prev = sortingRef.current;
                        const idx = prev.findIndex((s) => s.id === colId);

                        let next: SortingState;
                        if (idx === -1) {
                          next = [...prev, { id: colId, desc: false }];
                        } else if (!prev[idx].desc) {
                          next = prev.map((s) =>
                            s.id === colId ? { ...s, desc: true } : s,
                          );
                        } else {
                          next = prev.filter((s) => s.id !== colId);
                        }

                        sortingRef.current = next;
                        externalSorting!.onChange(next);
                      }
                    : undefined;

                  return (
                    <th
                      key={header.id}
                      className={styles.th}
                      data-align={align}
                      onClick={
                        externalSorting
                          ? handleExternalSortClick
                          : header.column.getToggleSortingHandler()
                      }
                      style={{
                        cursor: canSort ? "pointer" : "default",
                        ...(!autoWidth &&
                          size !== undefined && { width: size }),
                        ...getPinningStyle(header.column),
                      }}
                    >
                      <span className={styles.thContent}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        {canSort && (
                          <>
                            <Icon
                              iconName={
                                sorted === "asc"
                                  ? "SortingIconUp"
                                  : sorted === "desc"
                                    ? "SortingIconDown"
                                    : "SortingIcon"
                              }
                              className={styles.sortIcon}
                            />
                            {sortIndex >= 0 && (
                              <span className={styles.sortOrder}>
                                {sortIndex + 1}
                              </span>
                            )}
                          </>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading && !disableSkeleton ? (
              Array.from({ length: skeletonRows }).map((_, i) => (
                <tr key={i}>
                  {table.getAllLeafColumns().map((col) => {
                    const meta = col.columnDef.meta as
                      | DataTableColumnMeta
                      | undefined;
                    const align = meta?.align ?? "left";
                    const size = col.columnDef.size;
                    const autoWidth = meta?.autoWidth ?? false;
                    const skeletonRender = meta?.skeleton;
                    const content = skeletonRender ? (
                      skeletonRender()
                    ) : (
                      <Skeleton />
                    );

                    return (
                      <td
                        key={col.id}
                        className={styles.td}
                        data-align={align}
                        style={{
                          ...(!autoWidth &&
                            size !== undefined && { width: size }),
                          ...getPinningStyle(col),
                        }}
                      >
                        {align === "left" ? (
                          content
                        ) : (
                          <div
                            className={styles.cellAlignWrap}
                            data-align={align}
                          >
                            {content}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : table.getRowModel().rows.length === 0 && !isLoading ? (
              <tr>
                <td colSpan={columns.length} className={styles.empty}>
                  {emptyText}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  data-selected={row.getIsSelected() || undefined}
                >
                  {row.getVisibleCells().map((cell) => {
                    const align = getAlign(cell.column.columnDef);
                    const { cellClassName } = getMeta(cell.column.columnDef);
                    const expandOnHover =
                      (
                        cell.column.columnDef.meta as
                          | DataTableColumnMeta
                          | undefined
                      )?.expandOnHover ?? false;
                    const rendered = flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext(),
                    );

                    return (
                      <td
                        key={cell.id}
                        className={clsx(
                          styles.td,
                          cellClassName,
                          expandOnHover && styles.tdExpandOnHover,
                        )}
                        data-align={align}
                        style={getPinningStyle(cell.column)}
                      >
                        {align === "left" ? (
                          rendered
                        ) : (
                          <div
                            className={styles.cellAlignWrap}
                            data-align={align}
                          >
                            {rendered}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <Pagination
          currentPage={pagination.pageIndex + 1}
          itemsPerPage={pagination.pageSize}
          totalItems={data.length}
          onPageChange={(page) =>
            setPagination((p) => ({ ...p, pageIndex: page - 1 }))
          }
          onItemsPerPageChange={(items) =>
            setPagination({ pageIndex: 0, pageSize: items })
          }
        />
      )}
    </div>
  );
}

export default DataTable;
